// src/components/Production/ProductionDelaySummary.tsx
//
// EE-216 - the order-level roll-up of the per-production delay notes.
//
// An order can hold a dozen productions, each recording its own reason when
// its last QC step closed late. Reading that off the rows means scanning the
// whole table and remembering what you saw; this answers "why was this order
// late" in one line, above the table it summarises.
//
// Deliberately not merged into the Delay Reason already in the Order View
// header - that one is the order-completion feature's single field, set when
// the order itself is completed late. This is a different feature with a
// different trigger, and putting them in one place would suggest they are the
// same number.
//
// Reasons that only ever appear as contributing are shown too, muted: they
// carry no production count of their own, but leaving them out would hide
// half of what was recorded.
import { FunctionComponent } from "react";
import { Chip, Tooltip, Typography } from "@mui/material";
import { ProductionVMType } from "types/ProductionType";
import { useDelayReasons } from "hooks/useDelayReasons";

type PropsType = {
  productions: Pick<
    ProductionVMType,
    "delayPrimaryReason" | "delayContributingReasons"
  >[];
};

const ProductionDelaySummary: FunctionComponent<PropsType> = ({
  productions,
}) => {
  const { labelFor } = useDelayReasons();

  // How many productions each reason was the primary cause of. Insertion order
  // is irrelevant - the list is sorted by count below.
  const primaryCounts = new Map<string, number>();
  productions.forEach((production) => {
    const code = production.delayPrimaryReason;
    if (code) {
      primaryCounts.set(code, (primaryCounts.get(code) ?? 0) + 1);
    }
  });

  // A reason can contribute to several productions without ever being the
  // primary one. Counting those alongside the primaries would overstate them,
  // so they are listed without a count.
  const contributingOnly = new Set<string>();
  productions.forEach((production) => {
    (production.delayContributingReasons ?? []).forEach((code) => {
      if (!primaryCounts.has(code)) {
        contributingOnly.add(code);
      }
    });
  });

  if (primaryCounts.size === 0 && contributingOnly.size === 0) {
    return null;
  }

  const primaries = Array.from(primaryCounts.entries()).sort(
    (a, b) => b[1] - a[1] || labelFor(a[0]).localeCompare(labelFor(b[0]))
  );
  const contributing = Array.from(contributingOnly).sort((a, b) =>
    labelFor(a).localeCompare(labelFor(b))
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "0.4rem",
        margin: "0.5rem 0",
      }}
    >
      <Typography variant="body2" fontWeight="bold">
        Delay reasons
      </Typography>
      {primaries.map(([code, count]) => (
        <Tooltip
          key={code}
          title={`Primary reason on ${count} production${
            count === 1 ? "" : "s"
          }`}
          arrow
        >
          <Chip
            size="small"
            color="error"
            variant="outlined"
            label={count > 1 ? `${labelFor(code)} (${count})` : labelFor(code)}
          />
        </Tooltip>
      ))}
      {contributing.map((code) => (
        <Tooltip key={code} title="Contributing reason only" arrow>
          <Chip size="small" variant="outlined" label={labelFor(code)} />
        </Tooltip>
      ))}
    </div>
  );
};

export default ProductionDelaySummary;
